import React, { useEffect, useRef, useState } from "react";
import * as m from "../../lib/model";
import { RevenueMixBars } from "../RevenueMix";
import { Card, Tile } from "../shared";
import { RevenueSdeChart, CumulativeFcfChart, ValueBridgeChart } from "../charts";
import { ConsolidationView } from "../PlatformLink";
import { ExternalLink } from "lucide-react";
import { LegacyReturnsCard } from "../Recap";
import { qsbsBump } from "./Qsbs";
import { MarketHero } from "./MarketHero";
import { FinancialsHead, ReturnsHead } from "./DeckHeaders";
import { PLCard, TransactionCostsCard, CapitalisationCard } from "../FinancialBlocks";
import { SourcesUsesTables, suBreakdown } from "../SourcesUsesTables";
import { ReturnsBody } from "../ReturnsBody";
import { DistributionScheduleTable } from "../DistributionSchedule";
import { RollNote, RollRow } from "../RollNote";

const pct = (n) => (isNaN(n) ? "n/a" : m.fmtPct(n));
const tone = (d) => (m.dscrTone(d) === "go" ? "pos" : m.dscrTone(d) === "pass" ? "neg" : "neu");

export const Row = ({ l, v }) => (
  <div className="ir-row"><span className="lbl">{l}</span><span className="val mono">{v}</span></div>
);

export function OverviewTab({ M, title, platform }) {
  const cons = platform ? m.consolidation(M, platform) : null;
  const v = m.verdict(M);
  const dot = v === "GO" ? "go" : v === "PASS" ? "pass" : "amber";
  const ex = m.exitReturns(M), wf = m.lpWaterfall(M), cov = m.coverage(M), pf = m.proForma(M);
  const kpiBump = qsbsBump(M);
  return (
    <>
      {m.isRecap(M) && <div className="ir-gap"><LegacyReturnsCard M={M} testid="ir-legacy" compact /></div>}
      <div className={`verdict-why ${dot}`} data-testid="ir-why">
        <span className="vw-lbl">Why {v}:</span>
        {m.verdictReasons(M).map((r, i) => <span className="vw-chip" key={i}>{r}</span>)}
      </div>
      <div className="grid g4 ir-kpis">
        <Tile label="Asking Price" value={M.askPrice ? m.fmt$(M.askPrice) : "—"} sub={pf.pf ? "platform · add-ons in Sources & Uses" : undefined} testid="ir-kpi-ask" />
        <Tile label={pf.pf ? "Pro-forma Adj. SDE" : "Adj. SDE / EBITDA"} value={m.fmt$(pf.sde)} sub={pf.pf ? `platform ${m.fmt$(M.sde)} + ${pf.count} add-on${pf.count > 1 ? "s" : ""}` : `${m.fmtPct(m.grossMargin(M))} gross margin`} />
        <Tile label="Net Acquisition Cost" value={M.askPrice ? m.fmt$(m.nac(M)) : "—"} sub={M.askPrice && M.sde ? `${m.fmtX(m.entryMultiple(M))} entry${m.isRollupScope(M) ? " (consolidated)" : ""}` : ""} />
        <Tile label={pf.pf ? "Pro-forma Revenue" : "TTM Revenue"} value={m.fmt$(pf.revenue)} sub={pf.pf ? "at full deployment" : undefined} />
        <Tile label={`DSCR Year ${cov.year}`} value={m.fmtX(cov.dscr)} tone={m.dscrTone(cov.dscr)} sub={cov.pf ? "fully deployed" : undefined} testid="ir-kpi-dscr" />
        <Tile label="LP MOIC" value={m.fmtX(wf.lpMoic)} tone={wf.lpMoic >= (M.moicTarget || 2) ? "go" : "amber"}
          sub={kpiBump ? `QSBS +${m.fmtX(kpiBump.x)} after tax${kpiBump.unlevered ? " · unlevered" : ""}` : `Target ${m.fmtX(M.moicTarget || 2)}`} testid="ir-kpi-moic" />
        <Tile label="LP IRR" value={pct(wf.lpIrr)} tone={wf.lpIrr >= 15 ? "go" : "amber"}
          sub={kpiBump && kpiBump.bps > 0 ? `QSBS +${kpiBump.bps} bps after tax` : undefined} testid="ir-kpi-irr" />
        <Tile label="Hold Period" value={M.holdIndefinite ? "Indefinite" : `${M.hold} yrs`} sub={M.holdIndefinite ? "cash-yield strategy" : `${m.fmtX(M.exitMult)} exit`} />
      </div>
      {cons && <Card title={`Add-on Consolidation — ${platform.name} + ${title}`} className="ir-gap" testid="ir-consolidation"><ConsolidationView M={M} P={platform} c={cons} /></Card>}
      <div className="grid g2 ir-gap">
        <Card title="Investment Thesis">
          <div className="ir-thesis">
            <p><b>{title}</b> is a {M.industry || M.businessType || "small business"} platform{M.yearEstablished ? ` established in ${M.yearEstablished}` : ""}{M.employeeCount ? ` with ${M.employeeCount} employees` : ""}, generating {m.fmt$(M.ttmRevenue)} of trailing revenue and {m.fmt$(M.sde)} of adjusted SDE.</p>
            <p>The acquisition is structured as {M.dealType ? `${/^[aeiou]/i.test(M.dealType) ? "an" : "a"} ${M.dealType.toLowerCase()}` : "an acquisition"} funded with {m.fmt$(m.loanAmt(M))} of senior debt{M.snAmt > 0 ? `, a ${m.fmt$(M.snAmt)} seller note` : ""} and {m.fmt$(ex.equityInvested)} of equity{M.slbOn ? `, with ${m.fmt$(M.slbProceeds)} of sale-leaseback proceeds reducing the net basis` : ""}.</p>
            <p>Year-{cov.year} cash flow covers debt service {m.fmtX(cov.dscr)}{cov.pf ? " with every add-on owned for a full year" : ""} and the base case returns {m.fmtX(wf.lpMoic)} / {pct(wf.lpIrr)} to LP investors after {/^8/.test(String(M.prefReturn)) ? "an" : "a"} {M.prefReturn}% preferred return.</p>
          </div>
        </Card>
        <Card title="Equity Terms">
          <Row l="LP equity raise" v={m.fmt$(m.lpAskOf(M))} />
          <Row l="Preferred return" v={`${M.prefReturn}% cumulative`} />
          <Row l="LP / GP split after pref" v={`${M.lpSplit} / ${100 - M.lpSplit}`} />
          {M.moicStepUp > 0 && <Row l={`Above ${m.fmtX(M.moicStepUp)} MOIC`} v={`${100 - M.gpPromoteStep} / ${M.gpPromoteStep}`} />}
          <Row l="Target MOIC" v={m.fmtX(M.moicTarget || 2)} />
          <Row l="Management fee" v={M.mgmtFeePct > 0 ? `${M.mgmtFeePct}% of ${M.mgmtFeeBasis || "EBITDA"}` : "None"} />
          <Row l="Hold" v={M.holdIndefinite ? "Indefinite" : `${M.hold} years`} />
        </Card>
      </div>
    </>
  );
}

export function FinancialsTab({ M }) {
  const tax = (M.taxYears || []).filter((y) => y.year);
  const taxBy = tax.length ? Object.fromEntries(tax.map((y) => [String(y.year), y])) : null;
  const hist = (M.historicalYears || []).length ? M.historicalYears : tax.map((y) => ({ year: y.year, revenue: y.revenue, gm: 0, netIncome: y.netIncome, sde: y.taxSDE }));
  const cf = [["Adj. SDE", M.sde], ["Less: new owner salary", -M.salary], ["Less: CapEx", -M.capex],
    ...(M.mgmtFeePct > 0 ? [[`Less: management fee`, -m.mgmtFee(M)]] : []), ...(M.slbOn ? [["Less: leaseback rent", -m.annualRent(M)]] : []),
    ["CF available for debt service", m.cfads(M), true], ["Less: debt service", -m.totalAnnualDS(M)], ["Free cash flow", m.fcfYr1(M), true]];
  const cov = m.coverage(M), pf = m.proForma(M);
  const covRow = m.model5yr(M).find((r) => r.y === cov.year);
  const cfRows = cov.pf && covRow
    ? [["Adj. SDE / EBITDA — consolidated", covRow.sde_y], ["CF available for debt service", covRow.cfads, true],
      ["Less: debt service — platform + add-ons", -covRow.ds_y], ["Free cash flow", covRow.fcf, true]]
    : cf;
  return (
    <>
      <FinancialsHead M={M} />
      {(M.revenueMix || []).filter((r) => Number(r.value) > 0).length > 0 && (
        <Card title="Revenue mix" right={<span className="om-sub">share of {m.fmt$(M.ttmRevenue)} revenue</span>} testid="ir-revenue-mix">
          <RevenueMixBars mix={M.revenueMix} revenue={M.ttmRevenue} testid="ir-mix-bars" />
        </Card>
      )}
      {hist.length <= 1 ? (
        <Card title={hist.length ? `Trailing 12 Months — FY${hist[0].year}${pf.pf ? " · platform standalone" : ""}` : "Historical Financial Summary"} right={<span className="om-sub">{hist.length ? ((M.historicalYears || []).length ? "Source: CIM / financial statements" : "Source: filed tax returns") : ""}</span>} testid="ir-hist">
          {hist.length === 0 ? <div className="bullet" data-testid="ir-hist-empty">No historical financials have been parsed for this deal yet — upload the CIM or tax returns in Upload &amp; Parse and republish the room.</div> : (
            <div className="ir-metric-strip" data-testid="ir-hist-ttm" style={{ marginBottom: 0 }}>
              {[["Revenue", m.fmt$(hist[0].revenue)], ["Gross margin", hist[0].gm ? m.fmtPct(m.pctOf(hist[0].gm)) : "—"], ["Net income", m.fmt$(hist[0].netIncome)], ["Adj. SDE", m.fmt$(hist[0].sde || M.sde)],
                ...(taxBy && taxBy[String(hist[0].year)] ? [["Tax-return SDE", m.fmt$(taxBy[String(hist[0].year)].taxSDE)]] : [])
              ].map(([k, v]) => <div className="ir-metric" key={k}><span>{k}</span><b className="mono">{v}</b></div>)}
            </div>
          )}
        </Card>
      ) : (
      <div className="grid g2">
        <Card title={`Historical Financial Summary${pf.pf ? " — platform standalone" : ""}`} className="table-scroll" right={<span className="om-sub">{(M.historicalYears || []).length ? "Source: CIM / financial statements" : "Source: filed tax returns"}</span>} testid="ir-hist">
          <table className="tbl">
            <thead><tr><th>Fiscal Year</th><th className="num">Revenue</th><th className="num">GM %</th><th className="num">Net Income</th><th className="num">Adj. SDE</th>{taxBy && <th className="num">Tax-return SDE</th>}</tr></thead>
            <tbody>{hist.map((h) => {
              const t = taxBy && taxBy[String(h.year)];
              return (
                <tr key={h.year} data-testid={`ir-hist-${h.year}`}><td>{h.year}</td><td className="num">{m.fmt$(h.revenue)}</td><td className="num">{h.gm ? m.fmtPct(m.pctOf(h.gm)) : "—"}</td><td className={`num ${h.netIncome >= 0 ? "pos" : "neg"}`}>{m.fmt$(h.netIncome)}</td><td className="num">{m.fmt$(h.sde)}</td>
                  {taxBy && <td className="num">{t ? <>{m.fmt$(t.taxSDE)}{h.sde > 0 && t.taxSDE > 0 && <span className={`om-sub ${t.taxSDE < h.sde * 0.9 ? "neg" : ""}`} style={{ marginLeft: 6 }}>{((t.taxSDE / h.sde - 1) * 100).toFixed(0)}%</span>}</> : "—"}</td>}
                </tr>
              );
            })}</tbody>
          </table>
        </Card>
        <Card title="Revenue & SDE Trend"><RevenueSdeChart hy={hist} /></Card>
      </div>
      )}
      {tax.length > 0 && (
        <Card title="Per filed tax returns" className="table-scroll ir-gap" testid="ir-tax-years" right={<span className="om-sub">Parsed from uploaded 1120 / 1120-S / 1065 / Sch C</span>}>
          <table className="tbl">
            <thead><tr><th>Tax year</th><th className="num">Gross receipts</th><th className="num">Net income</th><th className="num">Officer comp</th><th className="num">D&amp;A</th><th className="num">Interest</th><th className="num">Tax-basis SDE</th><th className="num">Tax-basis EBITDA</th></tr></thead>
            <tbody>{tax.map((y) => (
              <tr key={y.year}><td>{y.year}</td><td className="num">{m.fmt$(y.revenue)}</td><td className={`num ${y.netIncome >= 0 ? "pos" : "neg"}`}>{m.fmt$(y.netIncome)}</td><td className="num">{m.fmt$(y.officerComp)}</td><td className="num">{m.fmt$(y.depreciation)}</td><td className="num">{m.fmt$(y.interest)}</td><td className="num"><b>{m.fmt$(y.taxSDE)}</b></td><td className="num">{m.fmt$(y.taxEBITDA)}</td></tr>
            ))}</tbody>
          </table>
          <div className="ir-foot-note">Tax-basis figures are as filed with the IRS and independently verify the seller-reported earnings above.</div>
        </Card>
      )}
      <div className="ir-gap">
        <Card title={`Year ${cov.year} Cash Flow${cov.pf ? " — fully deployed" : ""}`} testid="ir-cf-card">
          <table className="tbl"><tbody>
            {cfRows.map(([d, a, bold]) => <tr key={d} className={bold ? "hl" : ""}><td style={{ fontWeight: bold ? 700 : 400 }}>{d}</td><td className={`num ${bold ? (a >= 0 ? "pos" : "neg") : a < 0 ? "exp" : ""}`} style={{ fontWeight: bold ? 700 : 400 }}>{m.fmt$(a)}</td></tr>)}
          </tbody></table>
          <div className="ir-dscr-band"><span>DSCR Year {cov.year}</span><span className={`mono ${tone(cov.dscr)}`}>{m.fmtX(cov.dscr)}</span></div>
        </Card>
      </div>
      <div className="ir-gap"><PLCard M={M} V={M} testid="ir-pl-card" /></div>
    </>
  );
}

// Sources & Uses — the exact same tables, transaction-cost detail and capitalisation the app page shows.
export function SourcesUsesTab({ M }) {
  const consolidated = m.isRollupScope(M);
  const { su, totalDebt, totalEquity } = suBreakdown(M);
  const off = Math.abs(su.gap) > 1;
  const pctOf = (x) => (su.totalSources ? (x / su.totalSources) * 100 : 0);
  const debtPct = pctOf(totalDebt);
  const tc = m.transactionCosts(M);
  const ac = consolidated ? m.addonCosts(M) : { fees: 0 };
  const tcTotal = tc.total + (M.transExpenses || 0) + (consolidated ? ac.fees : 0);
  return (
    <>
      <div className="grid g4" style={{ marginBottom: 12 }}>
        <Tile label="Total Uses" value={m.fmt$(su.totalUses)} sub={m.isRecap(M) ? "Recap uses" : "Purchase price + costs"} testid="ir-su-tile-uses" />
        <Tile label="Total Sources" value={m.fmt$(su.totalSources)} sub={off ? `Out of balance by ${m.fmt$(su.gap)}` : "Balanced"} tone={off ? "pass" : "go"} testid="ir-su-tile-sources" />
        <Tile label="Total Debt" value={m.fmt$(totalDebt)} sub={`${debtPct.toFixed(1)}% of capitalisation`} testid="ir-su-tile-debt" />
        <Tile label="Total Equity" value={m.fmt$(totalEquity)} sub={`${pctOf(totalEquity).toFixed(1)}% of capitalisation`} testid="ir-su-tile-equity" />
      </div>
      <div className="grid g4" style={{ marginBottom: 16 }}>
        <Tile label="Entry Multiple" value={m.fmtX(m.entryMultiple(M))} sub={consolidated ? "Blended across platform + add-ons" : "NAC / EBITDA"} testid="ir-su-tile-mult" />
        <Tile label="Leverage" value={M.sde ? m.fmtX(totalDebt / M.sde) : "—"} sub="Total debt / EBITDA" testid="ir-su-tile-lev" />
        <Tile label="Transaction Costs" value={m.fmt$(tcTotal)} sub={consolidated ? "Platform + add-on fees in Uses" : "Fees + expenses in Uses"} testid="ir-su-tile-costs" />
        <Tile label="DSCR Year 1" value={m.fmtX(m.dscrYr1(M))} tone={m.dscrTone(m.dscrYr1(M))} testid="ir-su-tile-dscr" />
      </div>
      <div className="grid g2">
        <Card title={consolidated ? "Sources & Uses — Consolidated Roll-Up" : "Sources & Uses"} className="table-scroll" testid="ir-su-tables">
          <SourcesUsesTables V={M} width={760} />
        </Card>
        <div style={{ display: "flex", flexDirection: "column", gap: 12, minWidth: 0 }} data-testid="ir-su-right-col">
          <TransactionCostsCard V={M} testid="ir-su-costs" />
          <CapitalisationCard V={M} testid="ir-su-capitalisation" style={{ flex: 1 }} />
        </div>
      </div>
    </>
  );
}

export function ReturnsTab({ M }) {
  const [scen, setScen] = useState("base");
  const [view, setView] = useState("returns");
  const [variant, setVariant] = useState(null);
  const blOn = m.isBackLever(M);
  const base = variant && blOn ? (m.isRollupScope(M) ? m.rollupScopedModel({ ...M, addons: undefined, blUse: variant }) : { ...M, blUse: variant }) : M;
  const SM = m.scenarioModel(base, scen);
  const retBump = qsbsBump(SM);
  const rows = m.model5yr(SM), ex = m.exitReturns(SM), wf = m.lpWaterfall(SM);
  const plan = m.cashPlan(SM, rows);
  const covR = m.coverage(SM);
  const bl = plan.bl, blActive = !!(bl && bl.active);
  const useNow = (variant || M.blUse) === "distribute" ? "distribute" : "addons";
  // Returns and Sensitivity are very different heights — anchor the switch so the page never
  // appears to jump mid-table when the pane swaps.
  const viewBar = useRef(null);
  useEffect(() => {
    viewBar.current?.scrollIntoView({ block: "start", behavior: "smooth" });
  }, [view]);
  const sum = (arr, k) => arr.reduce((a, r) => a + (r[k] || 0), 0);
  return (
    <>
      <div className="ir-viewbar" ref={viewBar} data-testid="ir-returns-view">
        {[["returns", "Returns"], ["sensitivity", "Sensitivity"]].map(([k, l]) => (
          <button key={k} className={view === k ? "active" : ""} onClick={() => setView(k)} data-testid={`ir-view-${k}`}>{l}</button>
        ))}
      </div>
      {view === "sensitivity" ? <SensitivityTab M={M} /> : (
      <>
      <ReturnsHead M={SM} wf={wf} ex={ex} />
      <div className="ir-scen" data-testid="ir-scenario">
        {["downside", "base", "upside"].map((s) => <button key={s} className={scen === s ? "active" : ""} onClick={() => setScen(s)} data-testid={`ir-scen-${s}`}>{s}</button>)}
      </div>
      {blOn && (
        <div className="callout" style={{ marginBottom: 12, fontSize: 12, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }} data-testid="ir-backlever-note">
          <span>
            <b>Back-lever refinance:</b> {blActive
              ? <>in Year {bl.year} the platform{bl.refinancedAddons ? " and the add-ons already owned" : ""} are refinanced at {m.fmtX(bl.mult)} EBITDA (DSCR floor {m.fmtX(bl.target)}), raising <b>{m.fmt$(bl.proceeds)}</b> net of debt retired. {useNow === "distribute" ? <>All of it is returned to LPs as an early return of capital.</> : <><b>{m.fmt$(bl.toReserve)}</b> funds the add-on pipeline instead of capital calls{bl.toLp > 0 ? <>; the remaining <b>{m.fmt$(bl.toLp)}</b> is returned to LPs</> : null}.</>} New debt service applies from Year {bl.year + 1}.</>
              : <>modelled for Year {bl ? bl.year : M.blYear}, but the {m.fmtX(M.blDscr)} DSCR floor leaves no proceeds in the {scen} case.</>}
          </span>
          <div className="ir-scen" style={{ margin: 0 }} data-testid="ir-bl-variant">
            <button className={useNow === "distribute" ? "active" : ""} onClick={() => setVariant("distribute")} data-testid="ir-bl-variant-a">Variant A · refi to LPs</button>
            <button className={useNow === "addons" ? "active" : ""} onClick={() => setVariant("addons")} data-testid="ir-bl-variant-b">Variant B · refi into add-ons</button>
          </div>
        </div>
      )}
      {plan.retain && (
        <div className="callout" style={{ marginBottom: 12, fontSize: 12 }} data-testid="ir-cash-policy-note">
          <b>Distribution policy:</b> {100 - Math.round(m.payoutPctOf(SM))}% of the platform's after-debt free cash flow is retained on the balance sheet {plan.thru < m.effectiveHold(SM) - 1 ? `through Year ${plan.thru}` : "until exit"} ({m.fmt$(plan.totalRetained)} over the hold){m.retainUseOf(SM) === "addons" ? " as a war chest for future add-on acquisitions" : " as cash"}; the other {Math.round(m.payoutPctOf(SM))}% is distributed each year. Cash left on the balance sheet is returned to equity at exit.
        </div>
      )}
      <div className="grid g4" style={{ marginBottom: 16 }}>
        <Tile label="LP MOIC" value={m.fmtX(wf.lpMoic)} tone={wf.lpMoic >= (SM.moicTarget || 2) ? "go" : "amber"}
          sub={retBump ? `QSBS +${m.fmtX(retBump.x)} after tax${retBump.unlevered ? " · unlevered" : ""}` : undefined} testid="ir-ret-moic" />
        <Tile label="LP IRR" value={pct(wf.lpIrr)} tone={wf.lpIrr >= 15 ? "go" : "amber"}
          sub={retBump && retBump.bps > 0 ? `QSBS +${retBump.bps} bps after tax` : undefined} testid="ir-ret-irr" />
        <Tile label="Equity MOIC (deal)" value={m.fmtX(ex.moic)} />
        <Tile label={`Free Cash Flow Yr ${covR.year}`} value={m.fmt$((rows.find((r) => r.y === covR.year) || rows[0]).fcf)} sub={covR.pf ? "fully deployed" : undefined} tone={rows[0].fcf >= 0 ? "go" : "pass"} />
      </div>
      <ReturnsBody M={M} V={SM} scenario={scen} room />
      </>
      )}
    </>
  );
}

export function SensitivityTab({ M }) {
  const askMin = Math.max(100000, Math.round((M.askPrice || 500000) * 0.6));
  const priceCols = Array.from({ length: 5 }, (_, i) => Math.round(askMin + (((M.askPrice || 500000) * 1.2 - askMin) / 4) * i));
  const salaryRows = [0, 40000, 55000, 65000, 75000, 90000];
  const growthRows = [-5, 0, 2.5, 5, 10], multCols = [2, 3, 4, 5, 7];
  const scen = ["downside", "base", "upside"].map((s) => { const SM = m.scenarioModel(M, s); const ex = m.exitReturns(SM); const wf = m.lpWaterfall(SM); return { s, dscr: m.dscrYr1(SM), moic: ex.moic, irr: ex.irr, lpIrr: wf.lpIrr, lpMoic: wf.lpMoic, growth: SM.sdeGrowth, mult: SM.exitMult }; });
  return (
    <>
      <div className="grid g2">
        <Card title="DSCR — Buyer Salary × Price" className="table-scroll" testid="ir-sens-1">
          <table className="sens">
            <thead><tr><th>Salary \ Price</th>{priceCols.map((p) => <th key={p}>{m.fmt$(p)}</th>)}</tr></thead>
            <tbody>{salaryRows.map((sal) => <tr key={sal}><td className="rowh">{m.fmt$(sal)}</td>{priceCols.map((p) => { const d = m.dscrYr1({ ...M, salary: sal, askPrice: p }); return <td key={p} className={m.sensDSCR(d)}>{d.toFixed(2)}×</td>; })}</tr>)}</tbody>
          </table>
        </Card>
        <Card title="MOIC — Growth × Exit Multiple" className="table-scroll" testid="ir-sens-2">
          <table className="sens">
            <thead><tr><th>Growth \ Exit</th>{multCols.map((c) => <th key={c}>{c}×</th>)}</tr></thead>
            <tbody>{growthRows.map((g) => <tr key={g}><td className="rowh">{g}%</td>{multCols.map((c) => { const moic = m.exitReturns({ ...M, sdeGrowth: g, exitMult: c }).moic; return <td key={c} className={m.sensMOIC(moic)}>{moic.toFixed(2)}×</td>; })}</tr>)}</tbody>
          </table>
        </Card>
      </div>
      <div className="grid g2 ir-gap">
        <Card title="LP IRR — Growth × Exit Multiple" className="table-scroll" testid="ir-sens-3">
          <table className="sens">
            <thead><tr><th>Growth \ Exit</th>{multCols.map((c) => <th key={c}>{c}×</th>)}</tr></thead>
            <tbody>{growthRows.map((g) => <tr key={g}><td className="rowh">{g}%</td>{multCols.map((c) => { const irr = m.lpWaterfall({ ...M, sdeGrowth: g, exitMult: c }).lpIrr; return <td key={c} className={m.sensIRR(irr)}>{pct(irr)}</td>; })}</tr>)}</tbody>
          </table>
        </Card>
        <Card title="Scenario Comparison" className="table-scroll" testid="ir-scen-compare">
          <table className="tbl">
            <thead><tr><th>Metric</th>{scen.map((x) => <th className="num" key={x.s} style={{ textTransform: "capitalize" }}>{x.s}</th>)}</tr></thead>
            <tbody>
              <tr><td>SDE growth</td>{scen.map((x) => <td className="num" key={x.s}>{x.growth}%</td>)}</tr>
              <tr><td>Exit multiple</td>{scen.map((x) => <td className="num" key={x.s}>{m.fmtX(x.mult)}</td>)}</tr>
              <tr><td>DSCR Yr 1</td>{scen.map((x) => <td className={`num ${tone(x.dscr)}`} key={x.s}>{m.fmtX(x.dscr)}</td>)}</tr>
              <tr><td>Equity MOIC</td>{scen.map((x) => <td className="num" key={x.s}>{m.fmtX(x.moic)}</td>)}</tr>
              <tr><td>LP MOIC</td>{scen.map((x) => <td className="num" key={x.s}>{m.fmtX(x.lpMoic)}</td>)}</tr>
              <tr className="hl"><td><b>LP IRR</b></td>{scen.map((x) => <td className="num pos" key={x.s}><b>{pct(x.lpIrr)}</b></td>)}</tr>
            </tbody>
          </table>
        </Card>
      </div>
    </>
  );
}

const short = (t, n) => (t && t.length > n ? t.slice(0, n - 1).trimEnd() + "…" : t || "—");
const multShort = (x) => { if (!x) return "—"; const mm = String(x).match(/(\d+(?:\.\d+)?)\s*[x×]/i); return mm ? `${mm[1]}×` : short(String(x), 14); };
const compSource = (c, cits, i) => {
  const u = c.url || c.sourceUrl || c.source;
  if (u && /^https?:\/\//i.test(String(u))) return { url: u, title: "Read the press release / source" };
  const ref = String(c.note || "").match(/\[(\d+)\]/);
  const cit = (ref && cits[+ref[1] - 1]) || cits[i] || cits[0];
  return cit?.url ? { url: cit.url, title: `Cited source: ${cit.title || cit.url}` } : null;
};
const stripRefs = (t) => String(t || "").replace(/\s*\[\d+\]/g, "").trim();
const tamLine = (v) => { if (!v) return null; const t = typeof v === "string" ? v : v.value || v.label || ""; const mm = String(t).match(/~?\$[\d.,]+\s*[–-]?\s*(?:\$?[\d.,]+)?\s*[BMK]?\+?/i); return mm ? mm[0].trim() : short(String(t), 28); };

export function BuyerCards({ cons, title = "Likely Exit Buyers" }) {
  const ok = (v) => v && v !== "null";
  return (
    <div data-testid="ir-consolidators">
      <div className="section-label" style={{ marginTop: 0 }}>{title}</div>
      <div className="buyer-grid">
        {cons.map((c, i) => (
          <div className="buyer-card" key={i} data-testid={`ir-buyer-${i}`}>
            <div className="buyer-head"><b>{c.name}</b>{ok(c.type) && <span className="tag">{c.type}</span>}</div>
            <div className="buyer-kv">
              {ok(c.hq) && <div><span>HQ:</span> {c.hq}</div>}
              {ok(c.sponsor) && <div><span>Backed by:</span> {c.sponsor}</div>}
              {ok(c.appetite) && <div className="wide"><b>Appetite:</b> {c.appetite}</div>}
              {ok(c.recentDeals) && <div className="wide"><b>Recent deals:</b> {c.recentDeals}</div>}
            </div>
            {ok(c.note) && <div className="buyer-why">Why it matters: {c.note}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

export function MarketTab({ market, thesis = {} }) {
  if (!market) return <Card title="Market Intelligence"><div className="bullet">No market report was included in this room.</div></Card>;
  const comps = Array.isArray(market.comps) ? market.comps.slice(0, 6) : [];
  const cits = Array.isArray(market.compCitations) ? market.compCitations : [];
  const ok = (v) => v && v !== "null";
  const cons = Array.isArray(market.consolidators) ? market.consolidators.slice(0, 8) : [];
  const list = (k) => (Array.isArray(market[k]) ? market[k] : []);
  const tam = market.tam || {};
  const tiers = [["TAM", tam.value || tam.tam || tam.total, "Total addressable"], ["SAM", tam.sam, "Serviceable"], ["SOM", tam.som, "Obtainable"]].filter((t) => t[1]);
  return (
    <>
      <MarketHero market={market} block={thesis.marketBlock || {}} />
      {tiers.length > 0 && (
        <div className="mk-strip" data-testid="ir-tam">
          {tiers.map(([k, v, sub]) => <div className="mk-tier" key={k}><span className="mk-k">{k}</span><b className="mono">{tamLine(v)}</b><span className="mk-sub">{sub}</span></div>)}
          {(market.typicalMultiples?.range || market.sector) && <div className="mk-tier"><span className="mk-k">Multiples</span><b className="mono">{market.typicalMultiples?.range || "—"}</b><span className="mk-sub">{market.sector || "typical range"}</span></div>}
        </div>
      )}
      {market.marketOverview && <div className="mk-note" data-testid="ir-market-note">{short(market.marketOverview, 420)}</div>}
      {comps.length > 0 && (
        <Card title="Comparable Transactions" className="table-scroll ir-gap" testid="ir-comps">
          <table className="tbl mk-comps">
            <thead><tr><th>Deal</th><th>Location</th><th className="num">Revenue</th><th className="num">Multiple</th><th className="num">Year</th><th className="num">Source</th></tr></thead>
            <tbody>{comps.map((c, i) => {
              const src = compSource(c, cits, i);
              return (
                <tr key={i} data-testid={`ir-comp-${i}`}>
                  <td className="mk-deal">{c.descriptor || c.name}{ok(c.note) && <div className="mk-note-sub" data-testid={`ir-comp-note-${i}`}>{stripRefs(c.note)}</div>}</td>
                  <td>{c.location || "—"}</td>
                  <td className="num">{c.revenue ? m.fmt$(c.revenue) : "—"}</td>
                  <td className="num">{/disclosed|n\/a|unknown/i.test(String(c.multiple || "")) ? "—" : multShort(c.multiple)}</td>
                  <td className="num">{String(c.year || "—").match(/\d{4}s?/)?.[0] || "—"}</td>
                  <td className="num">{src ? <a className="mk-src" href={src.url} target="_blank" rel="noreferrer" title={src.title} data-testid={`ir-comp-src-${i}`}><ExternalLink size={13} /></a> : <span className="mk-src off">—</span>}</td>
                </tr>
              );
            })}</tbody>
          </table>
        </Card>
      )}
      <div className="grid g2 ir-gap">
        {cons.length > 0 && <div style={{ gridColumn: "1 / -1" }}><BuyerCards cons={cons} /></div>}
        {(list("tailwinds").length > 0 || list("headwinds").length > 0) && (
          <Card title="Tailwinds & Headwinds">
            {list("tailwinds").slice(0, 4).map((t, i) => <div className="bullet pos" key={`t${i}`}>+ {short(typeof t === "string" ? t : t.text || t.name, 120)}</div>)}
            {list("headwinds").slice(0, 3).map((t, i) => <div className="bullet" key={`h${i}`}>− {short(typeof t === "string" ? t : t.text || t.name, 120)}</div>)}
          </Card>
        )}
      </div>
    </>
  );
}

export function RisksTab({ M }) {
  const cv = m.coverage(M), dscr = cv.dscr, covY = cv.year, covPf = cv.pf, mult = m.entryMultiple(M), tr = m.revenueTrend(M);
  const strengths = [
    tr.declinePct <= 5 ? ["Durable top line", `${m.isRollupScope(M) ? "Platform revenue" : "Revenue"} of ${m.fmt$(M.ttmRevenue)} with ${tr.declinePct > 0 ? "stable" : "growing"} trend across the review period.`] : null,
    ["Entry multiple", `Entry at ${m.fmtX(mult)} on ${m.isRollupScope(M) ? "consolidated EBITDA (total price ÷ EBITDA)" : "adjusted SDE"}${M.slbOn ? " net of sale-leaseback proceeds" : ""}.`],
    m.grossMargin(M) >= 35 ? ["Healthy gross margins", `${m.fmtPct(m.grossMargin(M))} gross margin leaves room to absorb owner-salary normalisation.`] : null,
    M.snAmt > 0 ? ["Seller alignment", `A ${m.fmt$(M.snAmt)} seller note keeps the seller aligned and reduces senior leverage.`] : null,
    dscr >= 1.3 ? ["Coverage cushion", `Year-${covY} DSCR of ${m.fmtX(dscr)}${covPf ? " (fully deployed)" : ""} clears the 1.30× comfort threshold.`] : null,
    ["Established platform", `Founded ${M.yearEstablished || "—"}${M.employeeCount ? `, ${M.employeeCount} employees` : ""} — a proven, staffed operation.`],
  ].filter(Boolean);
  const risks = [
    ["SDE add-back concentration", `True operating earnings after a market salary are ≈ ${m.fmt$(m.trueEarnings(M))} (${(100 - m.sdeQuality(M)).toFixed(0)}% of SDE is add-backs).`],
    ["Debt service coverage", `Year-${covY} DSCR of ${m.fmtX(dscr)}${dscr < 1.3 ? " — below the 1.30× comfort threshold" : " — adequate, but sensitive to salary and price"}.`],
    tr.declinePct > 5 ? ["Revenue trend", `Revenue declined ${tr.declinePct.toFixed(0)}% over the period — diligence must explain the driver.`] : null,
    ["Owner dependency", "Key-person risk is elevated; crew and customer retention through transition is critical."],
    ["Customer concentration", `Diligence must confirm no single customer dominates the ${m.fmt$(M.ttmRevenue)} platform top line.`],
  ].filter(Boolean);
  return (
    <div className="grid g2">
      <Card title="Strengths" testid="ir-strengths">{strengths.map(([h, d]) => <div className="bullet" key={h}><b>{h}.</b> {d}</div>)}</Card>
      <Card title="Key Risks to Underwrite" testid="ir-risks">{risks.map(([h, d]) => <div className="bullet" key={h}><b>{h}.</b> {d}</div>)}</Card>
    </div>
  );
}

export function AssumptionsTab({ M, blind }) {
  return (
    <div className="grid g2">
      <Card title="Business & Deal">
        {!blind && <Row l="Business" v={M.dealName} />}
        <Row l="Industry" v={M.industry || M.businessType || "—"} />
        {!blind && <Row l="Location" v={M.address || "—"} />}
        <Row l="Deal type" v={M.dealType || "—"} />
        <Row l="Asking price" v={m.fmt$(M.askPrice)} />
        <Row l="TTM revenue" v={m.fmt$(M.ttmRevenue)} />
        <Row l="Adjusted SDE" v={m.fmt$(M.sde)} />
        <Row l="Gross margin" v={m.grossMargin(M) ? m.fmtPct(m.grossMargin(M)) : "—"} />
        <Row l="SDE growth" v={`${M.sdeGrowth}% / yr`} />
        <Row l="New owner salary" v={m.fmt$(M.salary)} />
        <Row l="Maintenance CapEx" v={m.fmt$(M.capex)} />
        <Row l="Working capital" v={m.fmt$(M.nwc)} />
      </Card>
      <Card title="Financing & Exit">
        <Row l="Down payment" v={`${M.downPct}% · ${m.fmt$(m.downAmt(M))}`} />
        <Row l="Senior debt" v={`${m.fmt$(m.loanAmt(M))} @ ${M.rate}% / ${M.loanYrs} yrs`} />
        <Row l="Seller note" v={M.snAmt > 0 ? `${m.fmt$(M.snAmt)} @ ${M.snRate}% / ${M.snYrs} yrs` : "None"} />
        <Row l="Seller rollover" v={M.rollPct > 0 ? `${M.rollPct}% · ${m.fmt$(m.rolloverAmt(M))}` : "None"} />
        <Row l="Sale-leaseback" v={M.slbOn ? `${m.fmt$(M.slbProceeds)} proceeds · ${m.fmt$(m.annualRent(M))}/yr rent` : "None"} />
        <Row l="Transaction costs" v={m.fmt$(m.transCostsTotal(M))} />
        <Row l="Hold period" v={M.holdIndefinite ? "Indefinite" : `${M.hold} yrs`} />
        <Row l="Exit multiple" v={m.fmtX(M.exitMult)} />
        <Row l="Preferred return" v={`${M.prefReturn}%`} />
        <Row l="LP / GP split" v={`${M.lpSplit} / ${100 - M.lpSplit}`} />
      </Card>
    </div>
  );
}
